import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
// sidebar handles navigation
import {
  FiSearch,
  FiPlus,
  FiEdit2,
  FiCheck,
  FiX,
  FiTrash2,
  FiMail,
  FiCalendar,
} from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import {
  getRetakeConfigs,
  createRetakeConfig,
  grantRetake,
  getRetakePermissions,
  getBeltConfigs,
  createBeltConfig,
  updateBeltConfig,
  deleteBeltConfig,
  updateRetakePermission,
  getAllEmployees,
} from "../api/client";
import Pagination from "../components/Pagination";

const AdminRetake = () => {
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("grant-retake");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Grant Retake Tab
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [retakeConfigs, setRetakeConfigs] = useState([]);
  const [selectedInterval, setSelectedInterval] = useState("");
  const [retakeReason, setRetakeReason] = useState("");
  const [sendNotification, setSendNotification] = useState(true);
  const [grantingRetake, setGrantingRetake] = useState(false);

  // Retake History Tab
  const [retakePermissions, setRetakePermissions] = useState([]);
  const [editingPermissionId, setEditingPermissionId] = useState(null);
  const [editPermissionValues, setEditPermissionValues] = useState({ retake_config_id: "immediate" });
  const [savingPermission, setSavingPermission] = useState(false);

  // Belt Calibration Tab
  const [beltConfigs, setBeltConfigs] = useState([]);
  const [editingBelt, setEditingBelt] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [savingBelt, setSavingBelt] = useState(false);
  const [addingBelt, setAddingBelt] = useState(false);
  const [newBelt, setNewBelt] = useState({
    belt_name: "",
    belt_color: "white",
    min_score: "",
    max_score: "",
  });

  // Retake Intervals Tab
  const [newIntervalDays, setNewIntervalDays] = useState("");
  const [creatingInterval, setCreatingInterval] = useState(false);

  // Pagination
  const [empPage, setEmpPage] = useState(1);
  const EMP_PAGE_SIZE = 20;

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [empRes, configsRes, permsRes, beltsRes] = await Promise.all([
          getAllEmployees(),
          getRetakeConfigs(),
          getRetakePermissions(),
          getBeltConfigs(),
        ]);

        setEmployees(empRes?.data || []);
        setRetakeConfigs(configsRes?.data || []);
        setRetakePermissions(permsRes?.data || []);
        setBeltConfigs(beltsRes?.data || []);

        const configs = configsRes?.data || [];
        if (configs.length > 0) {
          setSelectedInterval(configs[0].id);
        }
      } catch (err) {
        setError(err.message || "Failed to load data");
        console.error("Error loading AdminRetake data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle grant retake
  const handleGrantRetake = async (employeeId) => {
    if (!selectedInterval) {
      setError("Please select a retake interval or 'Immediate'");
      return;
    }

    try {
      setGrantingRetake(true);
      setError(null);

      await grantRetake({
        user_id: employeeId,
        retake_config_id: selectedInterval && selectedInterval !== "immediate" ? parseInt(selectedInterval) : null,
        reason: retakeReason || null,
        send_email: sendNotification,
      });

      // Reload permissions and employees
      const [permsRes, empRes] = await Promise.all([
        getRetakePermissions(),
        getAllEmployees(),
      ]);

      setRetakePermissions(permsRes?.data || []);
      setEmployees(empRes?.data || []);

      // Reset form
      setExpandedRow(null);
      setRetakeReason("");
      setSendNotification(true);
    } catch (err) {
      setError(err.message || "Failed to grant retake");
    } finally {
      setGrantingRetake(false);
    }
  };

  // Reschedule an existing retake permission
  const handleUpdatePermission = async (permissionId) => {
    try {
      setSavingPermission(true);
      setError(null);
      await updateRetakePermission(permissionId, {
        retake_config_id: editPermissionValues.retake_config_id,
      });
      const permsRes = await getRetakePermissions();
      setRetakePermissions(permsRes?.data || []);
      setEditingPermissionId(null);
      setEditPermissionValues({ retake_config_id: "immediate" });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to update retake");
    } finally {
      setSavingPermission(false);
    }
  };

  // Handle belt config update
  const handleSaveBelt = async (beltId) => {
    try {
      setSavingBelt(true);
      setError(null);

      await updateBeltConfig(beltId, {
        belt_name: editingValues.belt_name,
        belt_color: editingValues.belt_color,
        min_score: parseFloat(editingValues.min_score),
        max_score: parseFloat(editingValues.max_score),
      });

      // Reload belt configs
      const beltsRes = await getBeltConfigs();
      setBeltConfigs(beltsRes?.data || []);
      setEditingBelt(null);
      setEditingValues({});
    } catch (err) {
      setError(err.message || "Failed to update belt config");
    } finally {
      setSavingBelt(false);
    }
  };

  const handleCreateBelt = async () => {
    const name = (newBelt.belt_name || "").trim();
    const color = (newBelt.belt_color || "").trim();
    const minS = parseFloat(newBelt.min_score);
    const maxS = parseFloat(newBelt.max_score);

    if (!name || !color) {
      setError("Belt name and color are required");
      return;
    }
    if (Number.isNaN(minS) || Number.isNaN(maxS) || minS < 0 || maxS > 100 || minS > maxS) {
      setError("Enter a valid score range (0–100, min ≤ max)");
      return;
    }

    try {
      setSavingBelt(true);
      setError(null);
      await createBeltConfig({
        belt_name: name,
        belt_color: color,
        min_score: minS,
        max_score: maxS,
      });
      const beltsRes = await getBeltConfigs();
      setBeltConfigs(beltsRes?.data || []);
      setAddingBelt(false);
      setNewBelt({ belt_name: "", belt_color: "white", min_score: "", max_score: "" });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to create belt");
    } finally {
      setSavingBelt(false);
    }
  };

  const handleDeleteBelt = async (beltId, beltName) => {
    if (!window.confirm(`Delete "${beltName}"? This cannot be undone.`)) return;
    try {
      setSavingBelt(true);
      setError(null);
      await deleteBeltConfig(beltId);
      const beltsRes = await getBeltConfigs();
      setBeltConfigs(beltsRes?.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to delete belt");
    } finally {
      setSavingBelt(false);
    }
  };

  // Handle create retake interval
  const handleCreateInterval = async () => {
    if (!newIntervalDays || parseInt(newIntervalDays) <= 0) {
      setError("Please enter a valid number of days");
      return;
    }

    try {
      setCreatingInterval(true);
      setError(null);

      await createRetakeConfig({
        interval_days: parseInt(newIntervalDays),
        is_active: true,
      });

      // Reload configs
      const configsRes = await getRetakeConfigs();
      setRetakeConfigs(configsRes?.data || []);
      setNewIntervalDays("");
    } catch (err) {
      setError(err.message || "Failed to create retake interval");
    } finally {
      setCreatingInterval(false);
    }
  };

  // Filters for employees
  const filteredEmployees = employees.filter((emp) => {
    const query = searchQuery.toLowerCase();
    return (
      emp.name?.toLowerCase().includes(query) ||
      emp.email?.toLowerCase().includes(query) ||
      emp.business_unit?.toLowerCase().includes(query)
    );
  });

  // Get belt color — accepts belt_color ("white") or belt_name ("White Belt")
  const getBeltColor = (belt) => {
    const beltColors = {
      white: "#d1d5db",
      red: "#ef4444",
      orange: "#f97316",
      yellow: "#eab308",
      green: "#10b981",
      blue: "#3b82f6",
      black: "#1e1e1e",
    };
    const key = belt?.toLowerCase().replace(" belt", "").trim();
    return beltColors[key] || "#6b7280";
  };

  const BELT_COLOR_OPTIONS = [
    { value: "white", label: "White" },
    { value: "yellow", label: "Yellow" },
    { value: "green", label: "Green" },
    { value: "black", label: "Black" },
    { value: "red", label: "Red" },
    { value: "orange", label: "Orange" },
    { value: "blue", label: "Blue" },
  ];

  // Get belt display
  const getBeltLabel = (belt) => {
    return belt ? belt.charAt(0).toUpperCase() + belt.slice(1) : "None";
  };

  const tabVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
  };

  const containerStyle = {
    minHeight: "100vh",
    backgroundColor: "var(--bg-primary)",
    color: "var(--text-primary)",
    padding: "24px",
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
    gap: "16px",
  };

  const titleStyle = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
    minWidth: 0,
  };

  const tabContainerStyle = {
    display: "flex",
    gap: "8px",
    marginBottom: "20px",
    borderBottom: "1px solid var(--border-subtle)",
    overflowX: "auto",
  };

  const tabStyle = (active) => ({
    padding: "12px 20px",
    border: "none",
    background: "none",
    color: active ? "var(--color-highlight)" : "var(--text-secondary)",
    fontSize: "14px",
    fontWeight: active ? 600 : 400,
    cursor: "pointer",
    position: "relative",
    transition: "color 0.2s",
    borderBottom: active ? "2px solid var(--color-highlight)" : "none",
  });

  const contentCardStyle = {
    backgroundColor: "var(--bg-card)",
    border: "1px solid var(--border-subtle)",
    borderRadius: "var(--radius-md)",
    padding: "24px",
  };

  const searchStyle = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "var(--bg-primary)",
    border: "1px solid var(--border-subtle)",
    borderRadius: "var(--radius-md)",
    marginBottom: "20px",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
  };

  const thStyle = {
    textAlign: "left",
    padding: "12px",
    borderBottom: "1px solid var(--border-subtle)",
    fontSize: "12px",
    fontWeight: 600,
    color: "var(--text-secondary)",
    textTransform: "uppercase",
  };

  const tdStyle = {
    padding: "12px",
    borderBottom: "1px solid var(--border-subtle)",
    fontSize: "14px",
  };

  const buttonStyle = {
    padding: "8px 12px",
    background: "var(--color-highlight)",
    color: "white",
    border: "none",
    borderRadius: "var(--radius-md)",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 500,
    transition: "opacity 0.2s",
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    background: "var(--bg-primary)",
    color: "var(--text-primary)",
    border: "1px solid var(--border-subtle)",
  };

  const badgeStyle = (color) => ({
    display: "inline-block",
    padding: "4px 8px",
    backgroundColor: color,
    color: "white",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: 600,
  });

  const inputStyle = {
    padding: "8px 12px",
    backgroundColor: "var(--bg-primary)",
    border: "1px solid var(--border-subtle)",
    borderRadius: "var(--radius-md)",
    color: "var(--text-primary)",
    fontSize: "14px",
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer",
  };

  const formGroupStyle = {
    marginBottom: "16px",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    fontSize: "14px",
    fontWeight: 500,
    color: "var(--text-primary)",
  };

  const checkboxStyle = {
    marginRight: "8px",
    cursor: "pointer",
  };

  const expandedRowStyle = {
    backgroundColor: "var(--bg-primary)",
    padding: "20px",
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: "center", paddingTop: "60px" }}>
          <p style={{ color: "var(--text-secondary)" }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={{ marginBottom: "28px" }}>
        <h1 style={{ margin: "0 0 4px 0", fontSize: "24px", fontWeight: 700 }}>
          Assessment Retakes & Belt Calibration
        </h1>
      </div>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            padding: "12px 16px",
            backgroundColor: "#fee2e2",
            color: "#991b1b",
            borderRadius: "var(--radius-md)",
            marginBottom: "20px",
            fontSize: "14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "16px",
              color: "#991b1b",
            }}
          >
            ×
          </button>
        </motion.div>
      )}

      {/* Tabs */}
      <div style={tabContainerStyle}>
        {[
          { id: "grant-retake", label: "Grant Retake" },
          { id: "retake-history", label: "Retake History" },
          { id: "belt-calibration", label: "Belt Calibration" },
          { id: "retake-intervals", label: "Retake Intervals" },
        ].map((tab) => (
          <button
            key={tab.id}
            style={tabStyle(activeTab === tab.id)}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={tabVariants}
          transition={{ duration: 0.3 }}
          style={contentCardStyle}
        >
          {/* Grant Retake Tab */}
          {activeTab === "grant-retake" && (
            <div>
              <div style={searchStyle}>
                <FiSearch
                  style={{ color: "var(--text-secondary)", fontSize: "18px" }}
                />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setEmpPage(1); }}
                  style={{
                    flex: 1,
                    border: "none",
                    background: "none",
                    color: "var(--text-primary)",
                    fontSize: "14px",
                    outline: "none",
                  }}
                />
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid var(--border-subtle)" }}>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Email</th>
                      <th style={thStyle}>Organization</th>
                      <th style={thStyle}>Score</th>
                      <th style={thStyle}>Belt</th>
                      <th style={thStyle}>Attempts</th>
                      <th style={thStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.slice((empPage - 1) * EMP_PAGE_SIZE, empPage * EMP_PAGE_SIZE).map((emp, idx) => (
                      <React.Fragment key={emp.employee_id || idx}>
                        <tr>
                          <td style={tdStyle}>{emp.name || "—"}</td>
                          <td style={tdStyle}>{emp.email || "—"}</td>
                          <td style={tdStyle}>{emp.business_unit || "—"}</td>
                          <td style={tdStyle}>{emp.score_percentage != null ? `${emp.score_percentage}%` : "—"}</td>
                          <td style={tdStyle}>
                            {emp.belt && (
                              <span
                                style={badgeStyle(
                                  getBeltColor(emp.belt_color || emp.belt)
                                )}
                              >
                                {getBeltLabel(emp.belt)}
                              </span>
                            )}
                          </td>
                          <td style={tdStyle}>{emp.total_attempts || 0}</td>
                          <td style={tdStyle}>
                            {(() => {
                              const hasPending = emp.user_id && retakePermissions.some(
                                (p) => p.user_id === emp.user_id && !p.is_used
                              );
                              if (!emp.user_id) {
                                return (
                                  <button
                                    style={{
                                      ...buttonStyle,
                                      background: "#6b7280",
                                      cursor: "not-allowed",
                                      opacity: 0.7,
                                      fontSize: "12px",
                                      padding: "6px 10px",
                                      whiteSpace: "nowrap",
                                    }}
                                    disabled
                                    title="Employee has no account yet"
                                  >
                                    No Account
                                  </button>
                                );
                              }
                              if (hasPending && expandedRow !== emp.employee_id) {
                                return (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      padding: "6px 12px",
                                      borderRadius: "6px",
                                      background: "rgba(16, 185, 129, 0.15)",
                                      color: "#10b981",
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      whiteSpace: "nowrap",
                                    }}
                                    title="Retake already granted and not yet used"
                                  >
                                    ✓ Granted
                                  </span>
                                );
                              }
                              return (
                                <button
                                  style={buttonStyle}
                                  onClick={() =>
                                    setExpandedRow(
                                      expandedRow === emp.employee_id ? null : emp.employee_id
                                    )
                                  }
                                  title="Grant retake permission"
                                >
                                  {expandedRow === emp.employee_id ? "Hide" : "Grant"}
                                </button>
                              );
                            })()}
                          </td>
                        </tr>

                        {expandedRow === emp.employee_id && (
                          <tr>
                            <td colSpan="7" style={{ padding: 0 }}>
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                style={expandedRowStyle}
                              >
                                <div
                                  style={{
                                    maxWidth: "600px",
                                  }}
                                >
                                  <h4
                                    style={{
                                      margin: "0 0 16px 0",
                                      fontSize: "14px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Grant Retake for {emp.name}
                                  </h4>

                                  <div style={formGroupStyle}>
                                    <label style={labelStyle}>
                                      Retake Interval
                                    </label>
                                    <select
                                      value={selectedInterval}
                                      onChange={(e) =>
                                        setSelectedInterval(e.target.value)
                                      }
                                      style={selectStyle}
                                    >
                                      <option value="">
                                        Select interval...
                                      </option>
                                      <option value="immediate">
                                        Immediate (available now)
                                      </option>
                                      {retakeConfigs.map((config) => (
                                        <option
                                          key={config.id}
                                          value={config.id}
                                        >
                                          {config.name || `${config.interval_days} days`}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div style={formGroupStyle}>
                                    <label style={labelStyle}>
                                      Reason (Optional)
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="e.g., Performance improvement request"
                                      value={retakeReason}
                                      onChange={(e) =>
                                        setRetakeReason(e.target.value)
                                      }
                                      style={{
                                        ...inputStyle,
                                        width: "100%",
                                      }}
                                    />
                                  </div>

                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      marginBottom: "20px",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={sendNotification}
                                      onChange={(e) =>
                                        setSendNotification(e.target.checked)
                                      }
                                      style={checkboxStyle}
                                      id={`notify-${emp.employee_id}`}
                                    />
                                    <label
                                      htmlFor={`notify-${emp.employee_id}`}
                                      style={{
                                        margin: 0,
                                        cursor: "pointer",
                                        fontSize: "14px",
                                      }}
                                    >
                                      Send email notification
                                    </label>
                                  </div>

                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "12px",
                                    }}
                                  >
                                    <button
                                      style={buttonStyle}
                                      onClick={() =>
                                        handleGrantRetake(emp.user_id)
                                      }
                                      disabled={grantingRetake}
                                    >
                                      {grantingRetake
                                        ? "Granting..."
                                        : "Grant Retake"}
                                    </button>
                                    <button
                                      style={secondaryButtonStyle}
                                      onClick={() => setExpandedRow(null)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                <Pagination
                  currentPage={empPage}
                  totalPages={Math.ceil(filteredEmployees.length / EMP_PAGE_SIZE)}
                  onPageChange={setEmpPage}
                  totalItems={filteredEmployees.length}
                  pageSize={EMP_PAGE_SIZE}
                />
              </div>

              {filteredEmployees.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    color: "var(--text-secondary)",
                  }}
                >
                  No employees found
                </div>
              )}
            </div>
          )}

          {/* Retake History Tab */}
          {activeTab === "retake-history" && (
            <div style={{ overflowX: "auto" }}>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border-subtle)" }}>
                    <th style={thStyle}>User Name</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Granted By</th>
                    <th style={thStyle}>Reason</th>
                    <th style={thStyle}>Scheduled Date</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Email Sent</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {retakePermissions.map((perm, idx) => {
                    const statusDate = perm.scheduled_date
                      ? new Date(perm.scheduled_date)
                      : null;
                    const isScheduled =
                      statusDate && statusDate > new Date();
                    let statusLabel = "Pending";
                    let statusColor = "#eab308";

                    if (perm.is_used) {
                      statusLabel = "Completed";
                      statusColor = "#10b981";
                    } else if (isScheduled) {
                      statusLabel = "Scheduled";
                      statusColor = "#3b82f6";
                    }

                    const isEditing = editingPermissionId === perm.id;
                    return (
                      <React.Fragment key={perm.id || idx}>
                        <tr>
                          <td style={tdStyle}>{perm.user_name || "—"}</td>
                          <td style={tdStyle}>{perm.user_email || "—"}</td>
                          <td style={tdStyle}>{perm.granted_by_name || "—"}</td>
                          <td style={tdStyle}>{perm.reason || "—"}</td>
                          <td style={tdStyle}>
                            {perm.scheduled_date
                              ? new Date(perm.scheduled_date).toLocaleDateString()
                              : "—"}
                          </td>
                          <td style={tdStyle}>
                            <span style={badgeStyle(statusColor)}>
                              {statusLabel}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            {perm.email_sent ? (
                              <span style={badgeStyle("#10b981")}>Sent</span>
                            ) : (
                              <span style={badgeStyle("#6b7280")}>Not Sent</span>
                            )}
                          </td>
                          <td style={tdStyle}>
                            {perm.is_used ? (
                              <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>—</span>
                            ) : (
                              <button
                                style={{
                                  ...secondaryButtonStyle,
                                  padding: "6px 10px",
                                  fontSize: "12px",
                                }}
                                onClick={() => {
                                  if (isEditing) {
                                    setEditingPermissionId(null);
                                  } else {
                                    setEditingPermissionId(perm.id);
                                    setEditPermissionValues({
                                      retake_config_id: perm.retake_config_id
                                        ? String(perm.retake_config_id)
                                        : "immediate",
                                    });
                                  }
                                }}
                              >
                                <FiEdit2 style={{ marginRight: "4px", verticalAlign: "middle" }} />
                                {isEditing ? "Close" : "Reschedule"}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isEditing && (
                          <tr>
                            <td colSpan="8" style={{ padding: 0 }}>
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                style={expandedRowStyle}
                              >
                                <div style={{ maxWidth: "520px" }}>
                                  <h4 style={{ margin: "0 0 14px 0", fontSize: "14px", fontWeight: 600 }}>
                                    Reschedule retake for {perm.user_name}
                                  </h4>
                                  <div style={formGroupStyle}>
                                    <label style={labelStyle}>New Retake Interval</label>
                                    <select
                                      value={editPermissionValues.retake_config_id}
                                      onChange={(e) =>
                                        setEditPermissionValues({
                                          ...editPermissionValues,
                                          retake_config_id: e.target.value,
                                        })
                                      }
                                      style={selectStyle}
                                    >
                                      <option value="immediate">Immediate (available now)</option>
                                      {retakeConfigs.map((config) => (
                                        <option key={config.id} value={String(config.id)}>
                                          {config.name || `${config.interval_days} days`}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div style={{ display: "flex", gap: "12px" }}>
                                    <button
                                      style={buttonStyle}
                                      onClick={() => handleUpdatePermission(perm.id)}
                                      disabled={savingPermission}
                                    >
                                      {savingPermission ? "Saving..." : "Save Changes"}
                                    </button>
                                    <button
                                      style={secondaryButtonStyle}
                                      onClick={() => setEditingPermissionId(null)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              {retakePermissions.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    color: "var(--text-secondary)",
                  }}
                >
                  No retake permissions found
                </div>
              )}
            </div>
          )}

          {/* Belt Calibration Tab */}
          {activeTab === "belt-calibration" && (
            <div>
              <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <h3 style={{ margin: 0, fontSize: "16px" }}>
                  Belt Score Ranges
                </h3>
                {!addingBelt && (
                  <button
                    style={buttonStyle}
                    onClick={() => {
                      setAddingBelt(true);
                      setEditingBelt(null);
                      setError(null);
                    }}
                  >
                    <FiPlus style={{ marginRight: "6px", display: "inline" }} />
                    Add Belt
                  </button>
                )}
              </div>

                {/* Visual Preview Bar */}
                <div
                  style={{
                    display: "flex",
                    height: "40px",
                    borderRadius: "var(--radius-md)",
                    overflow: "hidden",
                    marginBottom: "20px",
                    border: "1px solid var(--border-subtle)",
                  }}
                >
                  {beltConfigs
                    .sort((a, b) => (a.min_score || 0) - (b.min_score || 0))
                    .map((belt, idx) => {
                      const range =
                        (belt.max_score || 100) - (belt.min_score || 0);
                      const width = `${(range / 100) * 100}%`;
                      const color = getBeltColor(belt.belt_color || belt.belt_name);

                      return (
                        <div
                          key={belt.id || idx}
                          style={{
                            width,
                            backgroundColor: color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "white",
                            textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                          }}
                          title={`${belt.belt_name}: ${belt.min_score}-${belt.max_score}`}
                        >
                          {belt.belt_name}
                        </div>
                      );
                    })}
                </div>

              {addingBelt && (
                <div
                  style={{
                    marginBottom: "20px",
                    padding: "16px",
                    backgroundColor: "var(--bg-primary)",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-subtle)",
                  }}
                >
                  <h4 style={{ margin: "0 0 12px 0", fontSize: "14px" }}>Add New Belt</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px", marginBottom: "12px" }}>
                    <div>
                      <label style={labelStyle}>Belt Name</label>
                      <input
                        type="text"
                        placeholder="e.g. Blue Belt"
                        value={newBelt.belt_name}
                        onChange={(e) => setNewBelt({ ...newBelt, belt_name: e.target.value })}
                        style={{ ...inputStyle, width: "100%" }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Color</label>
                      <select
                        value={newBelt.belt_color}
                        onChange={(e) => setNewBelt({ ...newBelt, belt_color: e.target.value })}
                        style={{ ...selectStyle, width: "100%" }}
                      >
                        {BELT_COLOR_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Min Score</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={newBelt.min_score}
                        onChange={(e) => setNewBelt({ ...newBelt, min_score: e.target.value })}
                        style={{ ...inputStyle, width: "100%" }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Max Score</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={newBelt.max_score}
                        onChange={(e) => setNewBelt({ ...newBelt, max_score: e.target.value })}
                        style={{ ...inputStyle, width: "100%" }}
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button style={buttonStyle} onClick={handleCreateBelt} disabled={savingBelt}>
                      {savingBelt ? "Creating..." : "Create Belt"}
                    </button>
                    <button
                      style={secondaryButtonStyle}
                      onClick={() => {
                        setAddingBelt(false);
                        setNewBelt({ belt_name: "", belt_color: "white", min_score: "", max_score: "" });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div style={{ overflowX: "auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr
                      style={{
                        borderBottom: "2px solid var(--border-subtle)",
                      }}
                    >
                      <th style={thStyle}>Belt Name</th>
                      <th style={thStyle}>Color</th>
                      <th style={thStyle}>Min Score</th>
                      <th style={thStyle}>Max Score</th>
                      <th style={thStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {beltConfigs.map((belt, idx) => (
                      <tr key={belt.id || idx}>
                        <td style={tdStyle}>
                          {editingBelt === belt.id ? (
                            <input
                              type="text"
                              value={editingValues.belt_name || ""}
                              onChange={(e) =>
                                setEditingValues({
                                  ...editingValues,
                                  belt_name: e.target.value,
                                })
                              }
                              style={{ ...inputStyle, width: "140px" }}
                            />
                          ) : (
                            <span
                              style={badgeStyle(
                                getBeltColor(belt.belt_color || belt.belt_name)
                              )}
                            >
                              {getBeltLabel(belt.belt_name)}
                            </span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {editingBelt === belt.id ? (
                            <select
                              value={editingValues.belt_color || ""}
                              onChange={(e) =>
                                setEditingValues({
                                  ...editingValues,
                                  belt_color: e.target.value,
                                })
                              }
                              style={{ ...selectStyle, width: "120px" }}
                            >
                              {BELT_COLOR_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  borderRadius: "50%",
                                  backgroundColor: getBeltColor(belt.belt_color),
                                  border: "1px solid var(--border-subtle)",
                                }}
                              />
                              {belt.belt_color}
                            </span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {editingBelt === belt.id ? (
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={editingValues.min_score ?? ""}
                              onChange={(e) =>
                                setEditingValues({
                                  ...editingValues,
                                  min_score: e.target.value,
                                })
                              }
                              style={{
                                ...inputStyle,
                                width: "80px",
                              }}
                            />
                          ) : (
                            belt.min_score
                          )}
                        </td>
                        <td style={tdStyle}>
                          {editingBelt === belt.id ? (
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={editingValues.max_score ?? ""}
                              onChange={(e) =>
                                setEditingValues({
                                  ...editingValues,
                                  max_score: e.target.value,
                                })
                              }
                              style={{
                                ...inputStyle,
                                width: "80px",
                              }}
                            />
                          ) : (
                            belt.max_score
                          )}
                        </td>
                        <td style={tdStyle}>
                          {editingBelt === belt.id ? (
                            <div style={{ display: "flex", gap: "8px" }}>
                              <button
                                style={{
                                  ...buttonStyle,
                                  padding: "6px 10px",
                                  fontSize: "12px",
                                }}
                                onClick={() => handleSaveBelt(belt.id)}
                                disabled={savingBelt}
                              >
                                <FiCheck />
                              </button>
                              <button
                                style={{
                                  ...secondaryButtonStyle,
                                  padding: "6px 10px",
                                  fontSize: "12px",
                                }}
                                onClick={() => {
                                  setEditingBelt(null);
                                  setEditingValues({});
                                }}
                              >
                                <FiX />
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: "flex", gap: "8px" }}>
                              <button
                                style={{
                                  ...secondaryButtonStyle,
                                  padding: "6px 10px",
                                  fontSize: "12px",
                                }}
                                onClick={() => {
                                  setEditingBelt(belt.id);
                                  setAddingBelt(false);
                                  setEditingValues({
                                    belt_name: belt.belt_name,
                                    belt_color: belt.belt_color,
                                    min_score: belt.min_score,
                                    max_score: belt.max_score,
                                  });
                                }}
                                title="Edit belt"
                              >
                                <FiEdit2 />
                              </button>
                              <button
                                style={{
                                  ...secondaryButtonStyle,
                                  padding: "6px 10px",
                                  fontSize: "12px",
                                  color: "#ef4444",
                                }}
                                onClick={() => handleDeleteBelt(belt.id, belt.belt_name)}
                                disabled={savingBelt}
                                title="Delete belt"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Retake Intervals Tab */}
          {activeTab === "retake-intervals" && (
            <div>
              <div
                style={{
                  marginBottom: "24px",
                  padding: "16px",
                  backgroundColor: "var(--bg-primary)",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border-subtle)",
                }}
              >
                <h3 style={{ margin: "0 0 12px 0", fontSize: "14px" }}>
                  Add New Interval
                </h3>
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-end",
                  }}
                >
                  <div style={{ flex: 1, maxWidth: "200px" }}>
                    <label style={labelStyle}>Days</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="e.g., 45"
                      value={newIntervalDays}
                      onChange={(e) => setNewIntervalDays(e.target.value)}
                      style={{
                        ...inputStyle,
                        width: "100%",
                      }}
                    />
                  </div>
                  <button
                    style={buttonStyle}
                    onClick={handleCreateInterval}
                    disabled={creatingInterval}
                  >
                    <FiPlus
                      style={{
                        marginRight: "6px",
                        display: "inline",
                      }}
                    />
                    {creatingInterval ? "Creating..." : "Add"}
                  </button>
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr
                      style={{
                        borderBottom: "2px solid var(--border-subtle)",
                      }}
                    >
                      <th style={thStyle}>Interval (Days)</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {retakeConfigs.map((config, idx) => (
                      <tr key={config.id || idx}>
                        <td style={tdStyle}>{config.interval_days} days</td>
                        <td style={tdStyle}>
                          <span
                            style={badgeStyle(
                              config.is_active ? "#10b981" : "#6b7280"
                            )}
                          >
                            {config.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          {config.created_at
                            ? new Date(config.created_at).toLocaleDateString()
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {retakeConfigs.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    color: "var(--text-secondary)",
                  }}
                >
                  No retake intervals configured yet
                </div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AdminRetake;
